import {
  REMOTE_SIGN_PROTOCOL_VERSION,
  REMOTE_SIGN_TTL_DAYS,
  remoteSignErr,
  type RemoteSignFacts,
  type RemoteSignLocale,
  type RemoteSignRequest,
  type RemoteSignResponse,
  type RemoteSignResult
} from '../../_shared/remoteSignProtocol.ts';

// The remote-signing service (Owner decision list 2026-10-19 items 4 / 45), Deno-free. Only the SHA-256 of a
// link's token is stored, so the database alone cannot open a signing page. One open link per inspection: a
// new link replaces the old one. A link works for 14 days and accepts exactly one signature.
export interface SignRow {
  id: string;
  userId: string;
  inspectionId: string;
  documentHtml: string;
  facts: RemoteSignFacts;
  locale: RemoteSignLocale;
  expiresAt: number;
  signedAt: number | null;
  result: RemoteSignResult | null;
}

export interface RemoteSignStore {
  insert(row: { userId: string; inspectionId: string; tokenHash: string; documentHtml: string; facts: RemoteSignFacts; locale: RemoteSignLocale; expiresAt: number }): Promise<string>;
  deleteForInspection(userId: string, inspectionId: string): Promise<void>;
  byTokenHash(tokenHash: string): Promise<SignRow | null>;
  byId(id: string, userId: string): Promise<SignRow | null>;
  // Writes the result only if the row is still unsigned; false = someone signed first.
  markSigned(id: string, result: RemoteSignResult): Promise<boolean>;
  remove(id: string, userId: string): Promise<void>;
}

export interface RemoteSignDeps {
  store: RemoteSignStore;
  now: () => number;
  randomToken: () => string;
  sha256Hex: (text: string) => Promise<string>;
}

export interface CallerFacts {
  // null for the public page (no account): it may only open and sign.
  userId: string | null;
  ip: string | null;
  userAgent: string | null;
}

const v = REMOTE_SIGN_PROTOCOL_VERSION;

export async function handleRemoteSign(request: RemoteSignRequest, caller: CallerFacts, deps: RemoteSignDeps): Promise<RemoteSignResponse> {
  const now = deps.now();
  switch (request.action) {
    case 'create': {
      if (!caller.userId) return remoteSignErr('unauthenticated', 'Sign in is required.');
      await deps.store.deleteForInspection(caller.userId, request.inspectionId);
      const token = deps.randomToken();
      const expiresAt = now + REMOTE_SIGN_TTL_DAYS * 86_400_000;
      const requestId = await deps.store.insert({
        userId: caller.userId,
        inspectionId: request.inspectionId,
        tokenHash: await deps.sha256Hex(token),
        documentHtml: request.documentHtml,
        facts: request.facts,
        locale: request.locale,
        expiresAt
      });
      return { ok: true, protocolVersion: v, action: 'create', requestId, token, expiresAt };
    }
    case 'status': {
      if (!caller.userId) return remoteSignErr('unauthenticated', 'Sign in is required.');
      const row = await deps.store.byId(request.requestId, caller.userId);
      if (!row) return remoteSignErr('notFound', 'This signing link no longer exists.');
      const state = row.signedAt !== null ? 'signed' : row.expiresAt <= now ? 'expired' : 'waiting';
      return { ok: true, protocolVersion: v, action: 'status', state, result: state === 'signed' ? row.result : null, expiresAt: row.expiresAt };
    }
    case 'discard': {
      if (!caller.userId) return remoteSignErr('unauthenticated', 'Sign in is required.');
      await deps.store.remove(request.requestId, caller.userId);
      return { ok: true, protocolVersion: v, action: 'discard' };
    }
    case 'open': {
      const row = await deps.store.byTokenHash(await deps.sha256Hex(request.token));
      if (!row) return remoteSignErr('notFound', 'This signing link is not valid.');
      // A signed or expired link no longer shows the document (it may hold the client's details).
      if (row.signedAt !== null) return { ok: true, protocolVersion: v, action: 'open', state: 'signed', documentHtml: '', facts: row.facts, locale: row.locale, expiresAt: row.expiresAt };
      if (row.expiresAt <= now) return remoteSignErr('expired', 'This signing link has expired.');
      return { ok: true, protocolVersion: v, action: 'open', state: 'waiting', documentHtml: row.documentHtml, facts: row.facts, locale: row.locale, expiresAt: row.expiresAt };
    }
    case 'sign': {
      const row = await deps.store.byTokenHash(await deps.sha256Hex(request.token));
      if (!row) return remoteSignErr('notFound', 'This signing link is not valid.');
      if (row.signedAt !== null) return remoteSignErr('alreadySigned', 'This agreement has already been signed.');
      if (row.expiresAt <= now) return remoteSignErr('expired', 'This signing link has expired.');
      const result: RemoteSignResult = { ...request.submission, signedAt: now, ip: caller.ip, userAgent: caller.userAgent };
      const written = await deps.store.markSigned(row.id, result);
      return written ? { ok: true, protocolVersion: v, action: 'sign', signedAt: now } : remoteSignErr('alreadySigned', 'This agreement has already been signed.');
    }
  }
}
